import { generators } from '@playcastdotio/Messaging';
import { ipcSend } from '../../../../api/messaging/protocols/ipc';
import type { PlaycastKeyDetail } from '@playcastdotio/CoreTypes';
import * as keycode from 'keyboard-key';
import { MacroPlaybackClock } from './MacroPlaybackClock';
import { MacroInputReleaseError } from './MacroInputReleaseError';

const DEFAULT_KEY_HOLD_MS = 20;

function createKeyDetail(keyCode: number, shift: boolean): PlaycastKeyDetail {
  return {
    keyCode,
    location: 'standard',
    source: { type: 'keyboard', code: keyCode },
    modifiers: { ctrl: false, alt: false, shift, meta: false },
    displayName: 'host',
  };
}

function sendKeyDown(keyCode: number, shift: boolean): void {
  ipcSend(generators.keyboard.down('host', createKeyDetail(keyCode, shift)));
}

function sendKeyUp(keyCode: number, shift: boolean): void {
  try { ipcSend(generators.keyboard.up('host', createKeyDetail(keyCode, shift))); }
  catch { throw new MacroInputReleaseError('keyboard'); }
}

async function sendKeyPress(keyCode: number, shift: boolean, hold: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) throw new Error('Macro cancelled');
  // Older recorder takes omitted tap duration and published ^0. Give those taps
  // the default keyboard hold so press/release cannot collapse into one instant.
  const holdMs = hold === 0 ? DEFAULT_KEY_HOLD_MS : hold;
  try { sendKeyDown(keyCode, shift); await MacroPlaybackClock.wait(holdMs, signal); }
  finally { sendKeyUp(keyCode, shift); }
  await MacroPlaybackClock.wait(20, signal);
}

const SHIFT_REQUIRED_CHARS = new Set([
  ')', '!', '@', '#', '$', '%', '^', '&', '*', '(',
  '_', '+', '{', '}', ':', '"', '<', '>', '?', '|',
]);

export class MacroKeyboardEmitter {
  public async sendKeyPress(keyCode: number, shift: boolean, hold: number, signal?: AbortSignal): Promise<void> {
    await sendKeyPress(keyCode, shift, hold, signal);
  }

  public async sendKeyString(text: string, signal?: AbortSignal): Promise<void> {
    for (const char of text) {
      if (signal?.aborted) throw new Error('Macro cancelled');
      const code = keycode.getCode(char);
      if (!code) continue;

      const shift =
        (char === char.toUpperCase() && char !== char.toLowerCase()) ||
        SHIFT_REQUIRED_CHARS.has(char);

      try {
        if (shift) sendKeyDown(16, shift);
        if (shift) await MacroPlaybackClock.wait(20, signal);
        await sendKeyPress(code, shift, 20, signal);
      } finally { if (shift) sendKeyUp(16, shift); }
      if (shift) await MacroPlaybackClock.wait(20, signal);
    }
  }
}
